import { useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { Bell, Search, User, ChevronDown, LogOut, Users } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { SidebarTrigger } from "@/components/ui/sidebar"
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { useAuth } from "@/hooks/useAuth"
import { useAuthorization } from "@/contexts/AuthorizationContext"
import { fetchWithAuth } from "@/lib/fetchWithAuth"
import { supabase } from "@/integrations/supabase/client"

interface SearchResult {
  id: string
  nome: string
  razaoSocial?: string
  email?: string
  telefone?: string
  status?: string
  tipo: 'cliente' | 'projeto'
}

// Função para extrair iniciais do nome (primeira letra do primeiro nome + primeira letra do último sobrenome)
const getInitials = (nome: string | undefined | null): string => {
  if (!nome) return 'U';
  
  const partes = nome.trim().split(/\s+/).filter(p => p.length > 0);
  
  if (partes.length === 0) return 'U';
  if (partes.length === 1) return partes[0][0].toUpperCase();
  
  // Primeira letra do primeiro nome + primeira letra do último sobrenome
  const primeira = partes[0][0].toUpperCase();
  const ultima = partes[partes.length - 1][0].toUpperCase();
  
  return primeira + ultima;
};

export function Header() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [notificacoesNaoLidas, setNotificacoesNaoLidas] = useState<number>(0);
  
  // Usar useAuthorization de forma defensiva para evitar tela branca
  let authz;
  try {
    authz = useAuthorization();
  } catch (e) {
    console.error("❌ Header: Erro ao usar useAuthorization:", e);
    authz = null;
  }

  const userProfile = authz?.userProfile;
  const userRole = authz?.userRole;

  // Buscar clientes quando o usuário digita
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const searchClients = async () => {
      setIsSearching(true);
      try {
        const response = await fetchWithAuth('/api/leads?crm_funil=vendas', {
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Erro ao buscar clientes');
        }

        const clients: any[] = await response.json();
        const queryLower = searchQuery.toLowerCase();
        
        // Filtrar clientes que correspondem à busca
        const filtered = clients
          .filter(client => {
            const nome = (client.nome || '').toLowerCase();
            const razaoSocial = (client.razaoSocial || '').toLowerCase();
            const email = (client.email || '').toLowerCase();
            const telefone = (client.telefone || '').replace(/\D/g, '');
            const searchTerm = searchQuery.replace(/\D/g, '');
            
            return nome.includes(queryLower) ||
                   razaoSocial.includes(queryLower) ||
                   email.includes(queryLower) ||
                   telefone.includes(searchTerm);
          })
          .slice(0, 10) // Limitar a 10 resultados
          .map(client => ({
            id: client.id,
            nome: client.nome || client.razaoSocial || 'Sem nome',
            razaoSocial: client.razaoSocial,
            email: client.email,
            telefone: client.telefone,
            status: client.status,
            tipo: 'cliente' as const
          }));

        setSearchResults(filtered);
      } catch (error) {
        console.error('Erro ao buscar clientes:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    // Debounce da busca
    const timeoutId = setTimeout(searchClients, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Abrir busca com Ctrl+K
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setSearchOpen((open) => !open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // Buscar contagem de notificações não lidas
  useEffect(() => {
    if (!userProfile?.id) {
      setNotificacoesNaoLidas(0);
      return;
    }

    const fetchNotificacoesNaoLidas = async () => {
      try {
        const { data, error } = await supabase
          .from('tarefas_notificacoes')
          .select('id')
          .eq('vendedor_id', userProfile.id)
          .eq('lida', false);

        if (error) {
          console.error('Erro ao buscar notificações não lidas:', error);
          return;
        }

        setNotificacoesNaoLidas(data?.length || 0);
      } catch (error) {
        console.error('Erro ao buscar notificações não lidas:', error);
      }
    };

    // Buscar imediatamente
    fetchNotificacoesNaoLidas();

    // Atualizar a cada 30 segundos
    const interval = setInterval(fetchNotificacoesNaoLidas, 30000);

    // Atualizar quando a janela recebe foco (usuário volta para a aba)
    const handleFocus = () => {
      fetchNotificacoesNaoLidas();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [userProfile?.id]);

  // Atualizar contador quando sair da página de notificações
  useEffect(() => {
    if (location.pathname !== '/notificacoes' && userProfile?.id) {
      // Pequeno delay para garantir que as mudanças na página de notificações foram salvas
      const timeoutId = setTimeout(() => {
        const fetchNotificacoesNaoLidas = async () => {
          try {
            const { data, error } = await supabase
              .from('tarefas_notificacoes')
              .select('id')
              .eq('vendedor_id', userProfile.id)
              .eq('lida', false);

            if (!error) {
              setNotificacoesNaoLidas(data?.length || 0);
            }
          } catch (error) {
            console.error('Erro ao atualizar contador de notificações:', error);
          }
        };
        fetchNotificacoesNaoLidas();
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [location.pathname, userProfile?.id]);

  const handleSelectResult = (result: SearchResult) => {
    setSearchOpen(false);
    setSearchQuery("");
    // Navegar para a página de clientes e abrir os detalhes do cliente selecionado
    navigate(`/clientes?id=${result.id}`);
  };

  const handleSearchAll = () => {
    if (searchQuery.trim()) {
      setSearchOpen(false);
      navigate(`/clientes?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery("");
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-16 items-center justify-between px-4">
        {/* Left side - Sidebar trigger and search */}
        <div className="flex items-center gap-4 flex-1">
          <SidebarTrigger />
          
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar clientes, projetos... (Ctrl+K)"
              className="pl-10 bg-muted/50 border-0 focus-visible:bg-background cursor-pointer"
              onClick={() => setSearchOpen(true)}
              readOnly
            />
          </div>
          
          <CommandDialog open={searchOpen} onOpenChange={setSearchOpen}>
            <CommandInput 
              placeholder="Buscar clientes, projetos..." 
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {isSearching && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  Buscando...
                </div>
              )}
              {!isSearching && searchQuery.trim() && searchResults.length === 0 && (
                <CommandEmpty>
                  <div className="py-6 text-center text-sm">
                    <p className="text-muted-foreground mb-2">Nenhum resultado encontrado</p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleSearchAll}
                      className="mt-2"
                    >
                      Ver todos os resultados
                    </Button>
                  </div>
                </CommandEmpty>
              )}
              {!isSearching && !searchQuery.trim() && (
                <CommandEmpty>
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    Digite para buscar clientes e projetos...
                  </div>
                </CommandEmpty>
              )}
              {searchResults.length > 0 && (
                <CommandGroup heading="Clientes">
                  {searchResults.map((result) => (
                    <CommandItem
                      key={result.id}
                      value={`${result.nome} ${result.razaoSocial || ''} ${result.email || ''}`}
                      onSelect={() => handleSelectResult(result)}
                      className="flex items-center gap-2"
                    >
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <div className="flex flex-col flex-1">
                        <span className="font-medium">{result.nome}</span>
                        {result.razaoSocial && result.razaoSocial !== result.nome && (
                          <span className="text-xs text-muted-foreground">{result.razaoSocial}</span>
                        )}
                        {result.email && (
                          <span className="text-xs text-muted-foreground">{result.email}</span>
                        )}
                      </div>
                      {result.status && (
                        <Badge variant="outline" className="text-xs">
                          {result.status}
                        </Badge>
                      )}
                    </CommandItem>
                  ))}
                  {searchQuery.trim() && (
                    <CommandItem onSelect={handleSearchAll} className="text-primary font-medium">
                      <Search className="h-4 w-4 mr-2" />
                      Ver todos os resultados para "{searchQuery}"
                    </CommandItem>
                  )}
                </CommandGroup>
              )}
            </CommandList>
          </CommandDialog>
        </div>

        {/* Right side - Actions and user */}
        <div className="flex items-center gap-3">
          {/* Notifications */}
          <Button 
            variant="ghost" 
            size="icon" 
            className="relative"
            onClick={() => navigate('/notificacoes')}
          >
            <Bell className="h-5 w-5" />
            {notificacoesNaoLidas > 0 && (
              <Badge 
                className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 text-xs bg-orange-500 text-white flex items-center justify-center"
              >
                {notificacoesNaoLidas > 99 ? '99+' : notificacoesNaoLidas}
              </Badge>
            )}
          </Button>

          {/* User dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-10 w-auto px-3">
                <div className="flex items-center gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src="/placeholder-avatar.jpg" alt="Avatar" />
                    <AvatarFallback className="bg-primary text-primary-foreground">
                      {getInitials(userProfile?.nome || user?.email?.split('@')[0])}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden md:flex flex-col items-start">
                    <span className="text-sm font-medium">
                      {userProfile?.nome || user?.email?.split('@')[0] || 'Usuário'}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {userRole === 'admin' ? 'Administrador' : 
                       userRole === 'representante' ? 'Representante' : 
                       userRole === 'parceiro' ? 'Parceiro' : 
                       userRole === 'vendedor' ? 'Vendedor' : 'Usuário'}
                    </span>
                  </div>
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">Usuário</p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {user?.email || "Não logado"}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/configuracoes?tab=perfil')}>
                <User className="mr-2 h-4 w-4" />
                <span>Perfil</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/notificacoes')}>
                <Bell className="mr-2 h-4 w-4" />
                <span>Notificações</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={async () => {
                try {
                  sessionStorage.setItem('sun7_logout_intent', '1');
                  await signOut();
                } catch (error) {
                  console.error('Erro no logout:', error);
                } finally {
                  window.location.href = '/auth';
                }
              }} className="text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Sair</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  )
}
